<template>
  <section class="form">
    <card-component title="Export Learner Engagements" icon="ballot">
      <div>
        <p>
          Select the date range you would like to export.
        </p>
        <p>
          Start date is currently limited to 1 month in the past i.e.
          {{ startDateLimitDisplay }}
        </p>
      </div>
      <b-field label="Start Date" horizontal>
        <b-datepicker
          placeholder="Click to select..."
          v-model="form.startDate"
          :min-date="dateLimits.dateMin"
          :max-date="dateLimits.dateMax"
          required
        >
        </b-datepicker>
      </b-field>
      <b-field label="End Date" horizontal>
        <b-datepicker
          placeholder="Click to select..."
          v-model="form.endDate"
          :min-date="dateLimits.dateMin"
          :max-date="dateLimits.dateMax"
          required
        >
        </b-datepicker>
      </b-field>
      <b-field label="School" horizontal>
        <b-select placeholder="Select a School" v-model="form.school" required>
          <option
            v-for="(school, index) in schools"
            :key="index"
            :value="school"
          >
            {{ school }}
          </option>
        </b-select>
      </b-field>
      <b-field grouped>
        <div class="control">
          <b-button
            type="is-primary is-success is-outlined"
            @click="reset()"
            v-html="'Reset'"
          />
        </div>
        <div class="control">
          <b-button
            type="is-primary is-success"
            @click="submit()"
            v-html="'Search'"
          />
        </div>
      </b-field>
      <span v-if="showTotalEngagements">
        <hr />
        <p>
          <strong>{{ totalEngagements }}</strong> engagement records found for
          this duration.
        </p>
      </span>
      <xlsx-workbook>
        <xlsx-sheet
          :collection="engagement.data"
          v-for="engagement in engagements"
          :key="engagement.name"
          :sheet-name="engagement.name"
        />
        <xlsx-download
          v-if="showDownloadBtn"
          :filename="`${this.downloadFileName}`"
        >
          <b-button type="is-primary is-success" v-html="'Download'" />
        </xlsx-download>
      </xlsx-workbook>
    </card-component>
  </section>
</template>

<script>
import CardComponent from "@/components/CardComponent";
import mapValues from "lodash/mapValues";
import { XlsxWorkbook, XlsxSheet, XlsxDownload } from "vue-xlsx";
import { mapGetters, mapState, mapActions } from "vuex";
import moment from "moment";

export default {
  name: "ExportEngagementsComponent",
  components: {
    CardComponent,
    XlsxWorkbook,
    XlsxSheet,
    XlsxDownload
  },
  data() {
    const today = new Date();
    return {
      isLoading: false,
      form: {
        startDate: null,
        endDate: null,
        school: null
      },
      downloadFileName: "reflective-learning.xlsx",
      dateLimits: {
        dateMin: new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          today.getDate()
        ),
        dateMax: new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        )
      },
      schools: [],
      engagements: [
        { name: "School Name", data: [{ message: "no data found" }] }
      ],
      startDateLimitDisplay: moment()
        .subtract(1, "month")
        .format("DD/MM/YYYY"),
      totalEngagements: 0,
      showTotalEngagements: false,
      showDownloadBtn: false
    };
  },
  computed: {
    ...mapState({
      projects: state => state.globalStore.projects
    })
  },
  mounted() {
    this.getSchools();
  },
  methods: {
    ...mapActions({
      loading: "SET_SCREEN_LOADING",
      schoolEngagements: "adminStore/fetchEngagements",
      schoolLearners: "adminStore/fetchLearners"
    }),
    getSchools() {
      for (let indexProject in this.projects) {
        if (this.projects[indexProject].schools[0]) {
          for (let indexSchool in this.projects[indexProject].schools) {
            if (this.projects[indexProject].schools[indexSchool].school) {
              this.schools.push(
                this.projects[indexProject].schools[indexSchool].school
              );
            }
          }
        }
      }
    },
    setDownloadFileName() {
      let startDate = moment(this.form.startDate).format("ddd DD-MM-YYYY");
      let endDate = moment(this.form.endDate).format("ddd DD-MM-YYYY");
      this.downloadFileName = `${this.form.school} engagements for ${startDate} - ${endDate}.xlsx`;
    },
    showDownloadStats(engagements) {
      this.totalEngagements = engagements.length;
      if (this.totalEngagements > 0) {
        this.showDownloadBtn = true;
      } else {
        this.showDownloadBtn = false;
      }
      this.showTotalEngagements = true;
    },
    filterEngagements(engagements, fetchedLearners) {
      let learnerStats = [];
      const startDate = moment(this.form.startDate).unix();
      const endDate = moment(this.form.endDate)
        .endOf("day")
        .unix();
      //add each days mins together
      engagements.forEach(bucket => {
        let userId = bucket._id;
        let rangeTotal = 0;
        bucket.days.forEach(day => {
          let dayTs = moment(day._id).unix();
          if (dayTs >= startDate && dayTs <= endDate) {
            let dayObj = moment(day._id).format("DD/MM/YYYY");
            if (!learnerStats[userId]) learnerStats[userId] = [];
            if (!learnerStats[userId][dayObj]) {
              learnerStats[userId][dayObj] = {};
              learnerStats[userId][dayObj].total = 0;
            }
            if (day.t <= 0) {
              return;
            }
            learnerStats[userId][dayObj].total += day.t;
          }
        });
      });
      //prepare for data for excel
      let excelDoc = [];
      for (let userId in learnerStats) {
        let usersDates = learnerStats[userId];
        let learnerDetails = fetchedLearners.find(
          learner => learner._id == userId
        );
        if (!learnerDetails) {
          // console.log("Learner not found!", userId);
          continue;
        }
        for (let date in usersDates) {
          excelDoc.push({
            userId: userId,
            "first name": learnerDetails.firstName,
            "last name": learnerDetails.lastName,
            email: learnerDetails.email,
            grade: learnerDetails.grade,
            project: learnerDetails.project,
            school: learnerDetails.school,
            date: date,
            total: learnerStats[userId][date].total
          });
        }
      }
      return excelDoc;
    },
    async submit() {
      this.showTotalEngagements = false;
      this.showDownloadBtn = false;
      this.loading(true);
      let response = await this.schoolEngagements(this.form.school);
      if (response.data && response.data.length) {
        this.engagements[0].name = response.name;
        //get the learners
        let fetchedLearners = await this.schoolLearners(this.form.school);
        //filter & calculate
        let filteredEngagements = this.filterEngagements(
          response.data,
          fetchedLearners
        );
        if (filteredEngagements && filteredEngagements.length) {
          this.engagements[0].data = filteredEngagements;
          this.setDownloadFileName();
          this.showDownloadStats(filteredEngagements);
        } else {
          this.showDownloadStats([]);
        }
      } else {
        this.showDownloadStats([]);
      }
      this.loading(false);
    },
    reset() {
      this.form = mapValues(this.form, item => {
        if (item && typeof item === "object") {
          return [];
        }
        return null;
      });
    }
  }
};
</script>
